// src/pages/usermanagement/UserList.jsx
// ✅ FIXED VERSION - loadUsers() changed to fetchUsers()
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Power, 
  Lock,
  Download,
  RefreshCw,
  Eye,
  User,
  Shield,
  Calendar,
  Clock,
  Mail,
  Phone
} from 'lucide-react';

// Services
import userService from '../../services/userService';

// Auth
import { 
  ProtectedButton, 
  HasPermission,
  RoleBadge 
} from '../../components/auth';
import { 
  PERMISSIONS, 
  USER_ROLES, 
  USER_STATUS,
  ROLE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  PAGINATION
} from '../../components/auth';

// Components
import DataTable from '../../components/common/DataTable';

const UserList = () => {
  const navigate = useNavigate();

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Pagination & Sorting
  const [pagination, setPagination] = useState({
    page: 0,
    size: PAGINATION.DEFAULT_SIZE,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: false,
  });
  const [sortConfig, setSortConfig] = useState({ 
    sortBy: 'username', 
    direction: 'ASC' 
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Fetch users
  const fetchUsers = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await userService.getUsers({
        page,
        size: pagination.size,
        sortBy: sortConfig.sortBy,
        sortDirection: sortConfig.direction,
        search: searchQuery,
        role: filterRole,
        status: filterStatus,
      });

      // ✅ Defensive check untuk berbagai format response
      console.log('API Response:', response);
      
      if (Array.isArray(response)) {
        setUsers(response);
        setPagination({
          page: 0,
          size: response.length,
          totalElements: response.length,
          totalPages: 1,
          first: true,
          last: true,
        });
      } else if (response && response.content) {
        setUsers(Array.isArray(response.content) ? response.content : []);
        setPagination({
          page: response.page || 0,
          size: response.size || 10,
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          first: response.first !== undefined ? response.first : true,
          last: response.last !== undefined ? response.last : false,
        });
      } else {
        console.warn('Unexpected response format:', response);
        setUsers([]);
        setPagination({
          page: 0,
          size: 10,
          totalElements: 0,
          totalPages: 0,
          first: true,
          last: true,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const stats = await userService.getUserStatistics();
      console.log('Statistics response:', stats);
      
      if (stats && typeof stats === 'object') {
        setStatistics(stats);
      } else {
        console.warn('Invalid statistics format:', stats);
        setStatistics(null);
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setStatistics(null);
    }
  };

  // Initial load
  useEffect(() => {
    fetchUsers(0);
    fetchStatistics();
  }, [sortConfig, filterRole, filterStatus]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        fetchUsers(0);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handlers
  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchUsers(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
    fetchUsers(0);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchStatistics();
    toast.success('Data refreshed');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterRole('');
    setFilterStatus('');
    setSortConfig({ sortBy: 'username', direction: 'ASC' });
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // ============================================
  // NAVIGATION HANDLERS - ✅ ALREADY CORRECT
  // ============================================
  
  const handleCreate = () => {
    navigate('/usermanagement/create');
  };

  const handleView = (row) => {
    // Optional: Implement user detail view
    // navigate(`/usermanagement/${row.id}`);
    //console.log('View user:', row);
    //toast.info('User detail view not yet implemented');
    navigate(`/usermanagement/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/usermanagement/${row.id}/edit`);
  };

  // ============================================
  // CRUD OPERATIONS
  // ============================================
  
  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete user "${row.username}"?`)) {
      return;
    }

    try {
      await userService.deleteUser(row.id);
      toast.success('User deleted successfully');
      fetchUsers(); // ✅ FIXED: Changed from loadUsers() to fetchUsers()
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
      console.error(error);
    }
  };

  // Export
  const handleExportExcel = async () => {
    try {
      toast.loading('Exporting to Excel...');
      await userService.downloadUsersExcel(
        { search: searchQuery, role: filterRole, status: filterStatus },
        `users_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      toast.dismiss();
      toast.success('Excel file downloaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.loading('Exporting to PDF...');
      await userService.downloadUsersPDF(
        { search: searchQuery, role: filterRole, status: filterStatus },
        `users_${new Date().toISOString().split('T')[0]}.pdf`
      );
      toast.dismiss();
      toast.success('PDF file downloaded');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
  };

  // Format datetime untuk display
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get relative time for last login
  const getRelativeTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 30) return `${diffDays} days ago`;
    return formatDateTime(dateTimeString);
  };

  // Get unique roles for filter
  const uniqueRoles = Object.keys(ROLE_LABELS);

  // Define columns for DataTable - Mockup Style
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {pagination.page * pagination.size + index + 1}
        </span>
      )
    },
    {
      key: 'username',
      label: 'Username',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-red-600" />
          </div>
          <span className="font-semibold text-red-600">{row.username}</span>
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-gray-900">{row.email}</span>
        </div>
      )
    },
    {
      key: 'fullName',
      label: 'Full Name',
      sortable: true,
      render: (row) => (
        <span className="text-gray-900 font-medium">{row.fullName}</span>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-gray-400" />
          <span className="text-gray-700">{row.phone || '-'}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          row.status === 'ACTIVE' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {STATUS_LABELS[row.status]}
        </span>
      )
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-purple-600" />
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
            {ROLE_LABELS[row.role]}
          </span>
        </div>
      )
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-400" />
          <div>
            <div className="text-sm text-gray-900">{getRelativeTime(row.lastLogin)}</div>
            <div className="text-xs text-gray-500">{formatDateTime(row.lastLogin)}</div>
          </div>
        </div>
      )
    },
    {
      key: 'createdAt',
      label: 'Created At',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-700">{formatDateTime(row.createdAt)}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2 justify-center">
          {/* View Button */}
          <button 
            onClick={() => handleView(row)} 
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>

          {/* Edit Button */}
          <HasPermission permission={PERMISSIONS.USER_UPDATE}>
            <button 
              onClick={() => handleEdit(row)} 
              className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
              title="Edit"
            >
              <Edit2 className="w-5 h-5" />
            </button>
          </HasPermission>

          {/* Delete Button */}
          <HasPermission permission={PERMISSIONS.USER_DELETE}>
            <button 
              onClick={() => handleDelete(row)} 
              className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Delete"
              disabled={row.role === 'ADMINISTRATOR'}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </HasPermission>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header - Mockup Style */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Data User</h1>
            <p className="text-gray-600 mt-1">Halaman untuk mengelola user</p>
          </div>

          <ProtectedButton
            permission={PERMISSIONS.USER_CREATE}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            onClick={handleCreate}
          >
            <Plus className="w-5 h-5" />
            Add User
          </ProtectedButton>
        </div>
      </div>

      {/* Statistics Cards - Gradient Style from Mockup */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Users */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Users</p>
                <p className="text-3xl font-bold mt-2">{statistics.totalUsers || 0}</p>
                <p className="text-blue-100 text-sm mt-2">Registered accounts</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Active Users</p>
                <p className="text-3xl font-bold mt-2">{statistics.activeUsers || 0}</p>
                <p className="text-green-100 text-sm mt-2">Currently active</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Inactive Users */}
          <div className="bg-gradient-to-br from-gray-600 to-gray-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-100 text-sm font-medium">Inactive Users</p>
                <p className="text-3xl font-bold mt-2">{statistics.inactiveUsers || 0}</p>
                <p className="text-gray-100 text-sm mt-2">Suspended accounts</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <User className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Total Roles */}
          <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Total Roles</p>
                <p className="text-3xl font-bold mt-2">{uniqueRoles.length}</p>
                <p className="text-purple-100 text-sm mt-2">Different roles</p>
              </div>
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Mockup Style with Pills */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="space-y-4">
          {/* Status Filter Pills */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-gray-700">Filter by Status:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterStatus('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({statistics?.totalUsers || 0})
              </button>
              <button
                onClick={() => setFilterStatus('ACTIVE')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === 'ACTIVE'
                    ? 'bg-green-600 text-white'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                Active ({statistics?.activeUsers || 0})
              </button>
              <button
                onClick={() => setFilterStatus('INACTIVE')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === 'INACTIVE'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Inactive ({statistics?.inactiveUsers || 0})
              </button>
            </div>
          </div>

          {/* Role Filter Pills */}
          <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-gray-700">Filter by Role:</span>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterRole('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterRole === ''
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Roles ({statistics?.totalUsers || 0})
              </button>
              {uniqueRoles.map(roleKey => (
                <button
                  key={roleKey}
                  onClick={() => setFilterRole(roleKey)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filterRole === roleKey
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                >
                  {ROLE_LABELS[roleKey]}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* DataTable Component - Using from Document 6 */}
      <DataTable
        columns={columns}
        data={users}
        currentPage={pagination.page}
        pageSize={pagination.size}
        totalElements={pagination.totalElements}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={loading}
        onSearch={handleSearch}
        searchValue={searchQuery}
        searchPlaceholder="Search by username, email, full name, or phone..."
        enableQuickJump={true}
        customPageSizes={[10, 25, 50, 100]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

export default UserList;